import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
  Divider,
  Link,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Person,
  Group,
  Assignment,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../services/api';
import PersonalDetailsForm from '../../components/pilot/PersonalDetailsForm';
import { RootState } from '../../store';
import { login, fetchCurrentUser } from '../../store/slices/authSlice';
import InlineEmailVerification from '../../components/auth/InlineEmailVerification';

interface Club {
  record_id: string;
  club_name: string;
  fee?: number;
  payment_info?: string;
  description?: string;
  is_visitor_club?: boolean;
}

interface PersonalDetails {
  record_id?: string;
  nome: string;
  cognome: string;
  // ... other fields from PersonalDetailsForm
}

const PilotRegistrationPageV2: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Password requirement validators
  const passwordRequirements = [
    {
      test: (p: string) => p.length >= 8,
      message: t('registration.passwordMin8'),
    },
    {
      test: (p: string) => /[A-Z]/.test(p),
      message: t('registration.passwordUppercase'),
    },
    {
      test: (p: string) => /[a-z]/.test(p),
      message: t('registration.passwordLowercase'),
    },
    {
      test: (p: string) => /[0-9]/.test(p),
      message: t('registration.passwordNumber'),
    },
    {
      test: (p: string) => /[@$!%*?&#_\-.]/.test(p),
      message: `${t('registration.passwordSpecial')} (@$!%*?&#_-.)`,
    },
  ];

  const steps = [
    { label: 'registration.steps.checkEmail', icon: <Person /> },
    { label: 'registration.steps.account', icon: <Person /> },
    { label: 'registration.steps.personalDetails', icon: <Assignment /> },
    { label: 'registration.steps.selectClub', icon: <Group /> },
    { label: 'registration.steps.clubRequirements', icon: <CheckCircle /> },
  ];
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showAddPilotRole, setShowAddPilotRole] = useState(false);
  const [showClubManagerLogin, setShowClubManagerLogin] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // State for each step
  const [accountData, setAccountData] = useState({
    email: '',
    nickname: '',
    password: '',
    repeatPassword: '',
    privacyConsent: false,
  });

  const [personalDetails, setPersonalDetails] =
    useState<PersonalDetails | null>(null);
  const [selectedClub, setSelectedClub] = useState<string>('');
  const [clubs, setClubs] = useState<Club[]>([]);
  const [registrationYear, setRegistrationYear] = useState<number>(new Date().getFullYear());
  const [showInlineVerification, setShowInlineVerification] = useState(false);
  const [showServiceAccountDialog, setShowServiceAccountDialog] = useState(false);
  const [serviceAccountInfo, setServiceAccountInfo] = useState<any>(null);

  const [clubRequirements, setClubRequirements] = useState({
    dichiarazione: false,
    gdpr: true, // Already accepted in step 1
    versamento: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const fetchClubs = async () => {
    try {
      const response = await api.get('/clubs-reg');
      setClubs(response.data);
    } catch (err) {
      console.error('Failed to fetch clubs:', err);
    }
  };

  const fetchRegistrationYear = async () => {
    try {
      const response = await api.get('/registration-year');
      setRegistrationYear(response.data.year);
    } catch (err) {
      console.error('Failed to fetch registration year:', err);
      // Fallback to current year if API fails
      setRegistrationYear(new Date().getFullYear());
    }
  };

  const checkPersonalDetails = useCallback(async (): Promise<boolean> => {
    if (!currentUser) return false;

    try {
      const response = await api.get('/personal-details/check-complete');
      if (response.data.has_personal_details && response.data.is_complete) {
        setPersonalDetails(response.data.personal_details);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to check personal details:', err);
      return false;
    }
  }, [currentUser]);

  // Separate effect for initialization to avoid race conditions
  useEffect(() => {
    // Always start from step 0 on page load, then check authentication
    fetchClubs();
    fetchRegistrationYear();

    // Only skip steps if the authenticated user is a pilot
    if (currentUser && isAuthenticated && currentUser.is_pilot) {
      checkPersonalDetails().then((hasDetails) => {
        if (hasDetails) {
          setActiveStep(3); // Skip to club selection
        } else {
          setActiveStep(2); // Go to personal details
        }
      });
    }
  }, [currentUser, isAuthenticated, checkPersonalDetails]); // Add dependencies

  const checkEmail = async () => {
    if (!accountData.privacyConsent) {
      setError(t('registration.errors.privacyRequired'));
      return;
    }

    if (!accountData.email) {
      setError(t('common.emailRequired'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(accountData.email)) {
      setError(t('registration.errors.invalidEmail'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First check for service account
      try {
        const serviceCheckResponse = await api.get(`/service-auth/check-pilot-registration-conflict/${encodeURIComponent(accountData.email)}`);
        if (serviceCheckResponse.data.has_conflict) {
          setServiceAccountInfo(serviceCheckResponse.data);
          setShowServiceAccountDialog(true);
          setLoading(false);
          return;
        }
      } catch (serviceErr) {
        console.log('Service account check failed, continuing...');
      }

      const response = await api.post('/check-pilot-email', null, {
        params: { email: accountData.email },
      });

      if (response.data.exists) {
        // User already exists

        if (response.data.is_pilot) {
          // User already has pilot capabilities - show login form
          // Note: Pilots don't need email verification anymore
          setShowLoginForm(true);
        } else if (response.data.is_club_manager && !response.data.is_pilot) {
          // Club manager without pilot role - need to login first
          setShowClubManagerLogin(true);
        }
      } else {
        // New user - proceed to account creation
        setActiveStep(1);
      }
    } catch (err: any) {
      console.error('Email check error:', err);
      setError(t('registration.errors.checkEmailFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setError(null);
    setResendSuccess(false);
    setLoading(true);

    try {
      // For pilots, use the send-verification-code endpoint that sends a PIN
      const response = await api.post('/send-verification-code', {
        email: accountData.email,
        language: i18n.language?.substring(0, 2) === 'it' ? 'it' : 'en',
      });

      if (response.data.success) {
        setResendSuccess(true);
        setShowResendVerification(false);
        setError(null);
        // Show the inline verification component
        setShowInlineVerification(true);
        setShowLoginForm(false);
        // Show success message
        setTimeout(() => {
          setResendSuccess(false);
        }, 5000);
      }
    } catch (err: any) {
      console.error('Failed to resend verification email:', err);
      setError(t('registration.errors.resendFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setError(null);
    setLoading(true);

    try {
      if (!accountData.password) {
        throw new Error(t('registration.errors.passwordRequired'));
      }

      await dispatch(
        login({
          username: accountData.email,
          password: accountData.password,
          login_type: 'pilot',
        }) as any
      ).unwrap();

      // After login, check if they have personal details
      const hasDetails = await checkPersonalDetails();
      if (hasDetails) {
        setActiveStep(3); // Skip to club selection
      } else {
        setActiveStep(2); // Go to personal details
      }
      setShowLoginForm(false);
    } catch (err: any) {
      // When using Redux createAsyncThunk with unwrap(), the error is the actual message
      let errorMessage = t('login.errors.loginFailed');
      
      // The error from Redux unwrap is usually a string message directly
      if (typeof err === 'string') {
        errorMessage = err;
        // Check if the error is about unverified email
        if (
          err.toLowerCase().includes('not been verified') ||
          err.toLowerCase().includes('email has not been verified')
        ) {
          setShowResendVerification(true);
          errorMessage =
            'Your email is not verified. Click below to resend the verification email.';
        }
      } else if (err?.response?.data?.detail) {
        // Fallback for direct API errors
        const errorDetail = err.response.data.detail;
        errorMessage = errorDetail;
        if (
          typeof errorDetail === 'string' &&
          (errorDetail.toLowerCase().includes('not been verified') ||
           errorDetail.toLowerCase().includes('email has not been verified'))
        ) {
          setShowResendVerification(true);
          errorMessage =
            'Your email is not verified. Click below to resend the verification email.';
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClubManagerLogin = async () => {
    setError(null);
    setLoading(true);

    try {
      if (!accountData.password) {
        throw new Error(t('registration.errors.passwordRequired'));
      }

      await dispatch(
        login({
          username: accountData.email,
          password: accountData.password,
          login_type: 'club_manager',
        }) as any
      ).unwrap();

      // After successful login, show the add pilot role form
      setShowClubManagerLogin(false);
      setShowAddPilotRole(true);
    } catch (err: any) {
      // When using Redux createAsyncThunk with unwrap(), the error is the actual message
      let errorMessage = t('login.errors.loginFailed');
      
      // The error from Redux unwrap is usually a string message directly
      if (typeof err === 'string') {
        errorMessage = err;
        // Check if the error is about unverified email
        if (
          err.toLowerCase().includes('not been verified') ||
          err.toLowerCase().includes('email has not been verified')
        ) {
          setShowResendVerification(true);
          errorMessage =
            'Your email is not verified. Click below to resend the verification email.';
        }
      } else if (err?.response?.data?.detail) {
        // Fallback for direct API errors
        const errorDetail = err.response.data.detail;
        errorMessage = errorDetail;
        if (
          typeof errorDetail === 'string' &&
          (errorDetail.toLowerCase().includes('not been verified') ||
           errorDetail.toLowerCase().includes('email has not been verified'))
        ) {
          setShowResendVerification(true);
          errorMessage =
            'Your email is not verified. Click below to resend the verification email.';
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPilotRole = async () => {
    if (!accountData.nickname) {
      setError(t('registration.errors.nicknameRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Ensure user is authenticated
      if (!isAuthenticated) {
        setError('You must be logged in to add pilot capabilities');
        return;
      }

      // Add pilot role to existing account
      await api.post('/add-pilot-role', {
        nickname: accountData.nickname,
      });

      // Update the user data in Redux to reflect the new pilot role
      await dispatch(fetchCurrentUser() as any);

      // After adding pilot role, proceed to personal details
      setShowAddPilotRole(false);
      setActiveStep(2);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || t('registration.errors.addPilotRoleFailed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountCreate = async () => {
    setError(null);
    setLoading(true);

    try {
      // Create new account
      if (!accountData.nickname || !accountData.password) {
        throw new Error(t('registration.errors.requiredFields'));
      }

      if (accountData.password !== accountData.repeatPassword) {
        throw new Error(t('registration.errors.passwordMismatch'));
      }

      if (accountData.password.length < 8) {
        throw new Error(t('registration.errors.passwordTooShort'));
      }

      // Create pilot account
      await api.post('/register-pilot', {
        email: accountData.email,
        nickname: accountData.nickname,
        password: accountData.password,
        repeat_password: accountData.repeatPassword,
        name: accountData.nickname, // Use nickname as name initially
        user_type: 'pilot',
        privacy_policy_accepted: true, // User accepted this in step 1
        club_agreement_accepted: false, // Not applicable for pilots
        club_agreement_version: null,
        language: (i18n.language?.substring(0, 2) === 'it' ? 'it' : 'en') as 'en' | 'it', // Extract language code, default to 'en'
      });

      // Pilots need to verify their email with a code
      // Show inline verification instead of auto-login
      setShowInlineVerification(true);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = t('registration.errors.accountCreationFailed');

      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        errorMessage =
          errorDetail[0].msg || errorDetail[0].message || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerified = async () => {
    setShowInlineVerification(false);
    setError(null); // Clear any previous errors

    // Auto-login the user after verification
    try {
      const formData = new URLSearchParams();
      formData.append('username', accountData.email);
      formData.append('password', accountData.password);
      formData.append('client_id', 'pilot');

      const loginResponse = await api.post('/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (loginResponse.data.access_token) {
        // Store the tokens
        localStorage.setItem('access_token', loginResponse.data.access_token);
        if (loginResponse.data.refresh_token) {
          localStorage.setItem(
            'refresh_token',
            loginResponse.data.refresh_token
          );
        }

        // Update axios default header
        api.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${loginResponse.data.access_token}`;

        // Fetch user data
        await dispatch(fetchCurrentUser() as any);

        // Move to personal details step
        setActiveStep(2);
      }
    } catch (err: any) {
      // Only show error if login actually failed
      console.error('Auto-login after verification failed:', err);
      // Check if we're actually logged in despite the error
      const token = localStorage.getItem('access_token');
      if (token) {
        // We're logged in, just continue
        await dispatch(fetchCurrentUser() as any);
        setActiveStep(2);
      } else {
        // Login actually failed
        setError(t('registration.errors.loginAfterVerificationFailed'));
      }
    }
  };

  const handlePersonalDetailsComplete = (details: PersonalDetails) => {
    setPersonalDetails(details);
    setActiveStep(3); // Go to club selection
  };

  const handleClubSelection = () => {
    if (!selectedClub) {
      setError(t('registration.errors.selectClub'));
      return;
    }
    setActiveStep(4); // Go to requirements
  };

  const handleFinalSubmit = async () => {
    setError(null);
    setLoading(true);

    try {
      // Validate club requirements (gdpr already accepted in step 1)
      if (!clubRequirements.dichiarazione || !clubRequirements.versamento) {
        throw new Error(t('registration.errors.acceptRequirements'));
      }

      // Create club registration (Card)
      const registrationData = {
        club_id: selectedClub,
        personal_details_id: personalDetails?.record_id,
        email: currentUser?.email || accountData.email,
        year: registrationYear, // Use registration year from backend BASE_YEAR setting
        richesta_adesione: clubRequirements.dichiarazione,
        privacy_policy: clubRequirements.gdpr, // Backend expects privacy_policy, not informative_registrazione
        impegno_versamento: clubRequirements.versamento,
      };

      await api.post('/pilot/register-club', registrationData);

      setSuccess(true);

      // Redirect to pilot dashboard after 3 seconds
      setTimeout(() => {
        navigate('/pilot-dashboard');
      }, 3000);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = t('registration.errors.registrationFailed');

      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        errorMessage =
          errorDetail[0].msg || errorDetail[0].message || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    switch (activeStep) {
      case 0:
        checkEmail();
        break;
      case 1:
        handleAccountCreate();
        break;
      case 2:
        // PersonalDetailsForm handles its own submission
        break;
      case 3:
        handleClubSelection();
        break;
      case 4:
        handleFinalSubmit();
        break;
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        // Email check step
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('registration.pilot.enterEmail')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('registration.pilot.checkAccount')}
            </Typography>

            {/* Privacy Consent - Must come first */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={accountData.privacyConsent}
                    onChange={(e) =>
                      setAccountData({
                        ...accountData,
                        privacyConsent: e.target.checked,
                      })
                    }
                    sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                  />
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('registration.privacy.title')}{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {t('registration.privacy.content')}
                    </Typography>
                    <Typography variant="body2">
                      {t('registration.privacy.linkText')}{' '}
                      <Link
                        href="https://www.hikeandfly.app/privacy"
                        target="_blank"
                        rel="noopener"
                        sx={{ color: 'primary.main' }}
                      >
                        {t('registration.privacy.privacyPolicy')}
                      </Link>{' '}
                      {t('registration.privacy.and')}{' '}
                      <Link
                        href="https://www.hikeandfly.app/terms"
                        target="_blank"
                        rel="noopener"
                        sx={{ color: 'primary.main' }}
                      >
                        {t('registration.privacy.termsOfService')}
                      </Link>
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </Paper>

            <TextField
              fullWidth
              label={t('registration.email')}
              type="email"
              value={accountData.email}
              onChange={(e) =>
                setAccountData({ ...accountData, email: e.target.value })
              }
              margin="normal"
              required
              disabled={loading || !accountData.privacyConsent}
              autoFocus={accountData.privacyConsent}
              InputProps={{
                readOnly: false,
              }}
            />

            {/* Show login form if existing pilot */}
            {showLoginForm && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('registration.pilot.welcomeBack')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t('registration.pilot.existingAccount')}
                </Typography>
                <TextField
                  fullWidth
                  label={t('registration.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={accountData.password}
                  onChange={(e) =>
                    setAccountData({ ...accountData, password: e.target.value })
                  }
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleLogin}
                      disabled={loading || !accountData.password}
                      startIcon={<LoginIcon />}
                    >
                      {t('login.signIn')}
                    </Button>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate('/forgot-password')}
                      sx={{ alignSelf: 'center' }}
                    >
                      {t('auth.forgotPassword')}
                    </Link>
                  </Box>
                  {showResendVerification && (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleResendVerification}
                      disabled={loading}
                      fullWidth
                    >
                      {t('users.resendVerificationEmail')}
                    </Button>
                  )}
                  {resendSuccess && (
                    <Alert severity="success">
                      {t('users.verificationEmailSent')}
                    </Alert>
                  )}
                  {!showResendVerification && (
                    <Typography variant="caption" color="text.secondary">
                      {t('registration.pilot.forgotPasswordHint')}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Show inline verification after resending email */}
            {showInlineVerification && !showLoginForm && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('registration.verifyEmail')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('registration.verificationCodeSent')}
                </Typography>
                
                <InlineEmailVerification
                  email={accountData.email}
                  onVerified={handleEmailVerified}
                  language={i18n.language === 'it' ? 'it' : 'en'}
                  autoSendCode={false} // Don't auto-send since we already sent in handleResendVerification
                />
              </Box>
            )}

            {/* Show login form for club managers */}
            {showClubManagerLogin && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('registration.pilot.club_manager_detected')}
                  </Typography>
                  <Typography variant="body2">
                    {t('registration.pilot.club_manager_login_required')}
                  </Typography>
                </Alert>
                <Typography variant="h6" gutterBottom>
                  {t('registration.pilot.login_to_add_pilot_role')}
                </Typography>
                <TextField
                  fullWidth
                  label={t('registration.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={accountData.password}
                  onChange={(e) =>
                    setAccountData({ ...accountData, password: e.target.value })
                  }
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleClubManagerLogin}
                      disabled={loading || !accountData.password}
                      startIcon={<LoginIcon />}
                    >
                      {t('login.signIn')}
                    </Button>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate('/forgot-password')}
                      sx={{ alignSelf: 'center' }}
                    >
                      {t('auth.forgotPassword')}
                    </Link>
                  </Box>
                  {showResendVerification && (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleResendVerification}
                      disabled={loading}
                      fullWidth
                    >
                      {t('users.resendVerificationEmail')}
                    </Button>
                  )}
                  {resendSuccess && (
                    <Alert severity="success">
                      {t('users.verificationEmailSent')}
                    </Alert>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {t('registration.pilot.clubManagerLoginHint')}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Show add pilot role form for club managers */}
            {showAddPilotRole && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('registration.pilot.add_pilot_role_title')}
                  </Typography>
                  <Typography variant="body2">
                    {t('registration.pilot.club_manager_add_pilot_info')}
                  </Typography>
                </Alert>
                <TextField
                  fullWidth
                  label={t('registration.pilot.nickname')}
                  value={accountData.nickname}
                  onChange={(e) =>
                    setAccountData({ ...accountData, nickname: e.target.value })
                  }
                  margin="normal"
                  required
                  helperText={t('registration.nicknameHelp')}
                />
                <Button
                  variant="contained"
                  onClick={handleAddPilotRole}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {t('registration.pilot.add_pilot_capabilities')}
                </Button>
              </Box>
            )}
          </Box>
        );

      case 1:
        // Show inline verification if needed
        if (showInlineVerification) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('registration.verifyEmail')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('registration.verificationCodeSent')}
              </Typography>

              <InlineEmailVerification
                email={accountData.email}
                onVerified={handleEmailVerified}
                language={i18n.language === 'it' ? 'it' : 'en'}
              />
            </Box>
          );
        }

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('registration.createAccount')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('registration.accountInfo')}
            </Typography>

            <TextField
              fullWidth
              label={t('registration.email')}
              type="email"
              value={accountData.email}
              margin="normal"
              required
              disabled={true}
              InputProps={{
                readOnly: true,
              }}
              helperText={t('registration.emailLocked')}
            />

            <TextField
              fullWidth
              label={t('registration.nickname')}
              value={accountData.nickname}
              onChange={(e) =>
                setAccountData({ ...accountData, nickname: e.target.value })
              }
              margin="normal"
              required
              disabled={loading}
              helperText={t('registration.nicknameHelp')}
            />

            <TextField
              fullWidth
              label={t('registration.password')}
              type={showPassword ? 'text' : 'password'}
              value={accountData.password}
              onChange={(e) =>
                setAccountData({ ...accountData, password: e.target.value })
              }
              margin="normal"
              required
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Requirements */}
            <Box
              sx={{
                mt: 1,
                mb: 2,
                p: 2,
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderRadius: 1,
                border: '1px solid rgba(25, 118, 210, 0.3)',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, mb: 1, color: 'text.primary' }}
              >
                {t('registration.passwordRequirements')}:
              </Typography>
              <Box sx={{ pl: 1 }}>
                {passwordRequirements.map((req, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                  >
                    {req.test(accountData.password) ? (
                      <CheckCircle
                        sx={{ fontSize: 16, color: 'success.main', mr: 1 }}
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          mr: 1,
                          minWidth: 16,
                          textAlign: 'center',
                        }}
                      >
                        •
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: req.test(accountData.password)
                          ? 'success.main'
                          : 'text.secondary',
                        fontWeight: req.test(accountData.password) ? 500 : 400,
                      }}
                    >
                      {req.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              label={t('registration.repeatPassword')}
              type={showRepeatPassword ? 'text' : 'password'}
              value={accountData.repeatPassword}
              onChange={(e) =>
                setAccountData({
                  ...accountData,
                  repeatPassword: e.target.value,
                })
              }
              margin="normal"
              required
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                      edge="end"
                    >
                      {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );

      case 2:
        return (
          <PersonalDetailsForm
            initialData={personalDetails}
            onComplete={handlePersonalDetailsComplete}
            isRegistrationFlow={true}
          />
        );

      case 3:
        return (
          <Box>
            {/* Club Selection - Prominent like original /registrations */}
            <Paper
              elevation={3}
              sx={{
                p: 3,
                mb: 4,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                {t('registration.selectYourClub')}
              </Typography>
              <Box sx={{ maxWidth: { xs: '100%', md: '600px' }, mx: 'auto' }}>
                <FormControl fullWidth required>
                  <Select
                    value={selectedClub}
                    onChange={(e) => setSelectedClub(e.target.value)}
                    disabled={loading}
                    displayEmpty
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 2,
                      '& .MuiSelect-select': {
                        py: 2,
                        fontSize: '1.1rem',
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <Typography sx={{ color: 'text.secondary' }}>
                        {t('registration.selectClub')}
                      </Typography>
                    </MenuItem>
                    {clubs.map((club) => (
                      <MenuItem key={club.record_id} value={club.record_id}>
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography sx={{ fontWeight: 600 }}>
                              {club.club_name}
                            </Typography>
                            {club.is_visitor_club && (
                              <Chip
                                label={t('registration.visitor')}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{
                                  ml: 1,
                                  borderColor: '#ff9800',
                                  color: '#ff9800',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 152, 0, 0.04)',
                                  },
                                }}
                              />
                            )}
                            {club.fee === 0 && (
                              <Chip
                                label={t('registration.free')}
                                size="small"
                                color="success"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                          {club.description && (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', mt: 0.5 }}
                            >
                              {club.description}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            {/* Club Details - if club is selected */}
            {selectedClub &&
              (() => {
                const selectedClubData = clubs.find(
                  (c) => c.record_id === selectedClub
                );
                return selectedClubData ? (
                  <>
                    {/* Club Information */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {selectedClubData.club_name}
                        </Typography>
                        {selectedClubData.is_visitor_club && (
                          <Chip
                            label={t('registration.visitor')}
                            size="small"
                            color="warning"
                            sx={{
                              backgroundColor: '#fff3e0',
                              color: '#f57c00',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>

                      {/* Club Description - Main Body */}
                      {selectedClubData.description && (
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'text.primary',
                              lineHeight: 1.8,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {selectedClubData.description}
                          </Typography>
                        </Box>
                      )}
                    </Paper>

                    {/* Visitor Club Notice */}
                    {selectedClubData.is_visitor_club && (
                      <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                          {t('registration.visitorClubNotice')}
                        </Typography>
                      </Alert>
                    )}

                    {/* Payment Information */}
                    <Paper sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 2,
                        }}
                      >
                        <Typography>
                          {t('registration.registrationFee')}:
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {selectedClubData.fee !== undefined &&
                          selectedClubData.fee > 0
                            ? `€${selectedClubData.fee}`
                            : t('registration.free')}
                        </Typography>
                      </Box>

                      {selectedClubData.payment_info && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            {t('registration.paymentInstructions')}:
                          </Typography>
                          <Typography variant="body2">
                            {selectedClubData.payment_info}
                          </Typography>
                        </Alert>
                      )}

                      {/* Future Digital Payment Banner */}
                      {selectedClubData.fee && selectedClubData.fee > 0 && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: 'primary.50',
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'primary.main',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: 'primary.main' }}
                              >
                                💳 {t('registration.digitalPayment')}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'primary.dark' }}
                              >
                                {t('registration.comingSoon')}
                              </Typography>
                            </Box>
                            <Chip
                              label={t('registration.comingSoon')}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>

                          {/* Payment Method Logos */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0.6,
                              mt: 1,
                            }}
                          >
                            {/* Visa */}
                            <Box
                              sx={{
                                height: 18,
                                width: 30,
                                bgcolor: '#1A1F71',
                                borderRadius: 0.3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography
                                sx={{
                                  color: 'white',
                                  fontSize: '0.5rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                VISA
                              </Typography>
                            </Box>

                            {/* Mastercard */}
                            <Box sx={{ display: 'flex' }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: '#EB001B',
                                }}
                              />
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: '#F79E1B',
                                  ml: -0.3,
                                }}
                              />
                            </Box>

                            {/* Apple Pay */}
                            <Box
                              sx={{
                                height: 18,
                                px: 0.5,
                                bgcolor: 'black',
                                borderRadius: 0.3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.2,
                              }}
                            >
                              <Typography
                                sx={{ color: 'white', fontSize: '0.5rem' }}
                              >
                                🍎
                              </Typography>
                              <Typography
                                sx={{
                                  color: 'white',
                                  fontSize: '0.5rem',
                                  fontWeight: 500,
                                }}
                              >
                                Pay
                              </Typography>
                            </Box>

                            {/* Google Pay */}
                            <Box
                              sx={{
                                height: 18,
                                px: 0.5,
                                bgcolor: 'white',
                                border: '1px solid #dadce0',
                                borderRadius: 0.3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.2,
                              }}
                            >
                              <Typography
                                sx={{ fontSize: '0.4rem', fontWeight: 500 }}
                              >
                                G
                              </Typography>
                              <Typography
                                sx={{
                                  color: '#4285f4',
                                  fontSize: '0.4rem',
                                  fontWeight: 500,
                                }}
                              >
                                Pay
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  </>
                ) : null;
              })()}
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('registration.sections.agreements')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('registration.acceptRequirements')}
            </Typography>

            {/* Membership Request */}
            <Paper sx={{ p: 3, mb: 2, bgcolor: 'grey.50' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={clubRequirements.dichiarazione}
                    onChange={(e) =>
                      setClubRequirements({
                        ...clubRequirements,
                        dichiarazione: e.target.checked,
                      })
                    }
                    disabled={loading}
                    sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                  />
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('registration.declarations.membershipRequest.title')}{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('registration.declarations.membershipRequest.content')}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </Paper>

            {/* Payment Commitment */}
            <Paper sx={{ p: 3, mb: 2, bgcolor: 'grey.50' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={clubRequirements.versamento}
                    onChange={(e) =>
                      setClubRequirements({
                        ...clubRequirements,
                        versamento: e.target.checked,
                      })
                    }
                    disabled={loading}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('registration.declarations.payment.title')}{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Typography variant="body2">
                      {(() => {
                        const selectedClubData = clubs.find(
                          (c) => c.record_id === selectedClub
                        );
                        return t('registration.declarations.payment.content', {
                          fee: selectedClubData?.fee
                            ? `€${selectedClubData.fee}`
                            : t('registration.free'),
                        });
                      })()}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {t('registration.success.emailSent')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('registration.success.checkEmail')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            <strong>{t('common.email')}:</strong> {accountData.email}
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              {t('registration.success.afterVerification')}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            {t('registration.success.goToLogin')}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
        {/* Page Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 4 } }}>
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(45deg, #3388ff 30%, #ff00fb 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('registration.pilot.joinClubTitle')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            {t('registration.pilot.joinClubSubtitle')}
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 2, sm: 4 } }}>
          {success ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle
                sx={{ fontSize: 80, color: 'success.main', mb: 3 }}
              />
              <Typography
                variant="h4"
                sx={{ mb: 3, color: 'success.main', fontWeight: 700 }}
              >
                {t('registration.success.title', 'Registration Successful!')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {t(
                  'registration.success.clubRegistrationComplete',
                  'Your club registration has been completed successfully.'
                )}
              </Typography>
              <Alert
                severity="success"
                sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
              >
                <Typography variant="body2">
                  {t(
                    'registration.success.emailSent',
                    'A confirmation email has been sent to your email address with your registration details and unique code.'
                  )}
                </Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t(
                  'registration.success.redirecting',
                  'Redirecting to your dashboard...'
                )}
              </Typography>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {/* Mobile: Show compact step indicator */}
              {isMobile ? (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('registration.step')} {activeStep + 1} / {steps.length}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#ff00fb', fontWeight: 600 }}>
                    {t(steps[activeStep].label)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                    {steps.map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: activeStep === index ? '#ff00fb' : activeStep > index ? '#4caf50' : '#e0e0e0',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                <Stepper
                  activeStep={activeStep}
                  sx={{
                    mb: 4,
                    '& .MuiStepLabel-root .Mui-completed': {
                      color: '#4caf50',
                    },
                    '& .MuiStepLabel-root .Mui-active': {
                      color: '#ff00fb',
                      fontWeight: 'bold',
                    },
                    '& .MuiStepLabel-label.Mui-completed': {
                      color: '#4caf50',
                      fontWeight: 500,
                    },
                    '& .MuiStepLabel-label.Mui-active': {
                      color: '#ff00fb',
                      fontWeight: 700,
                    },
                    '& .MuiStepConnector-line': {
                      borderColor: '#e0e0e0',
                    },
                    '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line':
                      {
                        borderColor: '#4caf50',
                      },
                    '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line':
                      {
                        borderColor: '#ff00fb',
                      },
                  }}
                >
                  {steps.map((step, index) => (
                    <Step key={index}>
                      <StepLabel
                        icon={step.icon}
                        StepIconProps={{
                          sx: {
                            '&.Mui-active': {
                              color: '#ff00fb',
                            },
                            '&.Mui-completed': {
                              color: '#4caf50',
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: activeStep === index ? 700 : 400,
                            color:
                              activeStep === index
                                ? '#ff00fb'
                                : activeStep > index
                                ? '#4caf50'
                                : 'text.secondary',
                          }}
                        >
                          {t(step.label)}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {renderStepContent()}

              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}
              >
                <Button
                  disabled={activeStep === 0 || loading}
                  onClick={handleBack}
                >
                  {t('common.back')}
                </Button>

                {activeStep !== 2 &&
                  !(activeStep === 1 && showInlineVerification) && (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={
                        loading ||
                        (activeStep === 4 &&
                          (!clubRequirements.dichiarazione ||
                            !clubRequirements.versamento))
                      }
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : activeStep === steps.length - 1 ? (
                        t('registration.submit')
                      ) : (
                        t('common.next')
                      )}
                    </Button>
                  )}
              </Box>
            </>
          )}
        </Paper>
      </Container>

      {/* Service Account Information Dialog */}
      <Dialog
        open={showServiceAccountDialog}
        onClose={() => setShowServiceAccountDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('registration.serviceAccount.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('registration.serviceAccount.message')}
          </Alert>
          
          <Typography variant="body1" paragraph>
            {t('registration.serviceAccount.explanation')}
          </Typography>
          
          {serviceAccountInfo && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('registration.serviceAccount.currentData')}:
              </Typography>
              <Typography variant="body2">
                • {t('registration.serviceAccount.equipmentItems', { count: serviceAccountInfo.equipment_count })}
              </Typography>
              <Typography variant="body2">
                • {t('registration.serviceAccount.activeBookings', { count: serviceAccountInfo.active_bookings })}
              </Typography>
            </Box>
          )}
          
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('registration.serviceAccount.benefits')}
          </Alert>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('registration.serviceAccount.dataPreserved')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowServiceAccountDialog(false);
              setActiveStep(1); // Continue with registration
            }}
            variant="contained"
            color="primary"
          >
            {t('registration.serviceAccount.continue')}
          </Button>
          <Button
            onClick={() => {
              setShowServiceAccountDialog(false);
              window.location.href = 'mailto:admin@hikeandfly.app?subject=Service%20Account%20Question';
            }}
            color="inherit"
          >
            {t('registration.serviceAccount.contactAdmin')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PilotRegistrationPageV2;
